<template>
    <section class="flex min-h-0 min-w-0 flex-col" aria-labelledby="goal-progress-title">
        <header class="shrink-0 border-b border-border pb-4">
            <h2 id="goal-progress-title" class="title-subsection flex items-center gap-3 text-ink">
                <DevelopmentJourneyHourglass class="size-[22px]" />{{ t("developmentJourneys.goalProgress.title") }}
            </h2>
        </header>

        <section
            scroll-region
            tabindex="0"
            :aria-label="t('developmentJourneys.goalProgress.sectionAriaLabel')"
            class="mt-5 min-h-0 space-y-5 focus-ring lg:flex-1 lg:overflow-y-auto lg:overscroll-contain lg:pr-1 lg:[scrollbar-gutter:stable]">
            <OverviewGoalProgress
                :goals="data.goals"
                :actions="data.actions"
                :goal-moments="data.goal_moments"
                :as-of-date="data.as_of_date"
                :timezone="data.timezone"
                :history-start-date="data.history_start_date">
                <slot />
            </OverviewGoalProgress>

            <BaseCard as="section" radius="lgr" class="@container" aria-labelledby="action-patterns-title">
                <h3 id="action-patterns-title" class="title-body text-ink">{{ t("developmentJourneys.goalProgress.actionPatternsTitle") }}</h3>
                <dl class="action-stats mt-5 grid grid-cols-2 gap-x-5 gap-y-6">
                    <div v-for="stat in stats" :key="stat.label" class="flex flex-col gap-1">
                        <dt class="body-small text-ink-muted">{{ stat.label }}</dt>
                        <dd class="display-sans-serif order-first text-ink tabular-nums">{{ stat.value }}</dd>
                    </div>
                </dl>
                <div class="mt-5 grid gap-5 border-t border-border pt-5 sm:grid-cols-2">
                    <div class="space-y-2" :aria-label="t('developmentJourneys.goalProgress.mostChosenAriaLabel')">
                        <p class="body-small text-ink-muted">{{ t("developmentJourneys.goalProgress.mostChosen") }}</p>
                        <ul v-if="patterns.most_chosen.length" class="space-y-3">
                            <li v-for="row in patterns.most_chosen" :key="row.channel">
                                <WeeklyActionTypeChip :channel="row.channel" />
                                <p class="body-small mt-1 text-ink-muted">
                                    {{ t("developmentJourneys.goalProgress.commitmentsCount", { count: row.committed, total: patterns.committed }) }}
                                </p>
                            </li>
                        </ul>
                        <p v-else class="body-small text-ink-muted">{{ t("developmentJourneys.goalProgress.mostChosenEmpty") }}</p>
                    </div>
                    <div
                        class="space-y-2 border-t border-border pt-5 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-5"
                        :aria-label="t('developmentJourneys.goalProgress.mostUnfinishedAriaLabel')">
                        <p class="body-small text-ink-muted">{{ t("developmentJourneys.goalProgress.mostUnfinished") }}</p>
                        <ul v-if="patterns.most_unfinished.length" class="space-y-3">
                            <li v-for="row in patterns.most_unfinished" :key="row.channel">
                                <WeeklyActionTypeChip :channel="row.channel" />
                                <p class="body-small mt-1 text-ink-muted">
                                    {{ t("developmentJourneys.goalProgress.unfinishedCommitmentsCount", { count: row.unfinished, total: row.past_committed }) }}
                                </p>
                            </li>
                        </ul>
                        <p v-else class="body-small text-ink-muted">
                            {{
                                patterns.past_commitments ? t("developmentJourneys.goalProgress.pastCommitmentsComplete") : t("developmentJourneys.goalProgress.noPastCommitments")
                            }}
                        </p>
                    </div>
                </div>
            </BaseCard>
        </section>
    </section>
</template>

<script setup lang="ts">
import type { GoalProgressResponse } from "~vue/api/generated/development-journeys";
import BaseCard from "~vue/components/BaseCard.vue";
import DevelopmentJourneyHourglass from "~vue/icons/DevelopmentJourneyHourglass.vue";
import OverviewGoalProgress from "~vue/pages/developmentJourneys/progressInsights/OverviewGoalProgress.vue";
import WeeklyActionTypeChip from "~vue/pages/developmentJourneys/WeeklyActionTypeChip.vue";
import { formatNumber } from "~vue/utils/formatNumber";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { data } = defineProps<{ data: GoalProgressResponse }>();
const patterns = computed(() => data.action_patterns);
// Locale catalogue can finish loading after mount, so stat labels must stay reactive to the active locale.
const stats = computed(() => {
    const summary = data.action_patterns;
    return [
        { label: t("developmentJourneys.goalProgress.actionsCompleted"), value: formatNumber(summary.completed) },
        { label: t("developmentJourneys.goalProgress.actionsCommitted"), value: formatNumber(summary.committed) },
        { label: t("developmentJourneys.goalProgress.weeksWithCompletions"), value: formatNumber(summary.completion_weeks) },
        { label: t("developmentJourneys.goalProgress.followThroughStat"), value: summary.follow_through === null ? "—" : formatNumber(summary.follow_through / 100, "percent") },
    ];
});
</script>

<style scoped>
@layer components {
    @container (min-width: 36rem) {
        .action-stats {
            grid-template-columns: repeat(4, minmax(0, 1fr));
        }
    }
}
</style>
